import { FirebaseProfile, FirestoreAccount, FirestoreFlags } from '@trace/types';
import { auth, firestore } from 'firebase/app';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { parse, serialize } from 'cookie';
import jwtDecode from 'jwt-decode';
import useAuthentication, { AuthenticationState } from './useAuthentication';
import useSDKDetectionUntilSeen from '../SDKIndicator/useSDKDetectionUntilSeen';
import { NS_FIREBASE_TOKEN } from '../../constants/LocalStorage';
import { callCloud } from '../../fetch/fetch';
import { AuthContextTrace } from './AuthenticateTrace';

export default function useAuthenticationFirebase(): AuthenticationState<FirebaseProfile> {
  const { token: traceToken, data: traceData } = useContext(AuthContextTrace);
  const [firebaseToken, setFirebaseToken] = useState('');
  const [accountUpdates, setAccountUpdates] = useState<FirestoreAccount | undefined>();
  const [flagsUpdates, setFlagUpdates] = useState<FirestoreFlags | undefined>();

  const refAccount = useMemo(() => firestore().doc(`/accounts/${traceData.appId}`), [
    traceData.appId,
  ]);
  const refDetections = useMemo(() => firestore().doc(`/detections/${traceData.appId}`), [
    traceData.appId,
  ]);
  const refFlags = useMemo(() => firestore().doc(`/flags/${traceData.appId}`), [traceData.appId]);
  const detectionUpdates = useSDKDetectionUntilSeen();

  // Get the Firebase token from cookie.
  const getStoredToken = useCallback(() => {
    const token = parse(document.cookie)[NS_FIREBASE_TOKEN];

    if (token) {
      const { uid } = jwtDecode(token) as { uid: string };

      if (uid === traceData.appId) {
        return token;
      }
    }

    return null;
  }, [traceData.appId]);

  // Return the account details from the JWT
  const getData = useCallback(async () => {
    const account = accountUpdates || (await refAccount.get()).data();
    const detections = detectionUpdates || (await refDetections.get()).data();
    const flags = flagsUpdates || (await refFlags.get()).data();

    return {
      ...account,
      ...detections,
      ...flags,
    } as FirebaseProfile;
  }, [accountUpdates, detectionUpdates, flagsUpdates, refAccount, refDetections, refFlags]);

  // Fetch a new Firebase token from the cloud function.
  const getNewToken = useCallback(async () => {
    return (
      await callCloud<{ token: string }>(
        'POST',
        '/trace/auth/firebase',
        traceToken || '',
        {},
        {
          credentials: 'include',
        },
      )
    ).token;
  }, [traceToken]);

  // Store the Firebase token inside a cookie.
  const setToken = useCallback(async (token: string) => {
    await auth().signInWithCustomToken(token);
    const { exp } = jwtDecode(token) as { uid: string; exp: number };
    document.cookie = serialize(NS_FIREBASE_TOKEN, token, {
      expires: new Date(exp * 1000),
      domain: process.env.APM_DOMAIN,
      path: '/',
    });
    setFirebaseToken(token);
  }, []);

  // Clears the Firebase token
  const clearToken = () => {
    document.cookie = serialize(NS_FIREBASE_TOKEN, '', {
      expires: new Date(1),
      domain: process.env.APM_DOMAIN,
      path: '/',
    });
  };

  useEffect(() => {
    const accountUnsubscribe = refAccount.onSnapshot((snapshot) => {
      setAccountUpdates(snapshot.data() as FirestoreAccount);
    });

    const flagUnsubscribe = refFlags.onSnapshot((snapshot) => {
      setFlagUpdates(snapshot.data() as FirestoreFlags);
    });

    return () => {
      accountUnsubscribe();
      flagUnsubscribe();
    };
  }, [firebaseToken, refAccount, refFlags]);

  return useAuthentication({
    clearToken,
    getData,
    getNewToken,
    getStoredToken,
    setToken,
  });
}
